import { useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Cell, LabelList, ResponsiveContainer, Tooltip, ReferenceLine,
  LineChart, Line, CartesianGrid
} from 'recharts';
import { useDashboardConfig } from '../context/DashboardContext';

function formatValue(value, unit) {
  if (value === null || value === undefined) return 'N/A';
  if (unit === 'percent') return `${value.toFixed(1)}%`;
  if (unit === 'dollars') return `$${Math.round(value).toLocaleString()}`;
  if (unit === 'index') return value.toFixed(2);
  return value.toLocaleString();
}

// Custom SVG label for the goal reference line — "Goal" left of the line, value right
function GoalLabel({ viewBox, goal, unit, accentColor, fontFamily }) {
  if (!viewBox) return null;
  const { x, y } = viewBox;
  const labelY = y - 4;
  return (
    <g>
      <text x={x - 5} y={labelY} textAnchor="end" fontSize={10} fontWeight="600"
        fill={accentColor} fontFamily={fontFamily}>
        Goal
      </text>
      <text x={x + 5} y={labelY} textAnchor="start" fontSize={10}
        fill={accentColor} fontFamily={fontFamily}>
        {formatValue(goal, unit)}
      </text>
    </g>
  );
}

function getYearValue(trendArr, year) {
  if (!trendArr || !year) return null;
  return trendArr.find(p => p.year === year)?.value ?? null;
}

function LegendLine({ stroke, strokeDasharray, strokeWidth = 2 }) {
  return (
    <svg width="16" height="10" style={{ flexShrink: 0 }}>
      <line
        x1="0" y1="5" x2="16" y2="5"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeDasharray={strokeDasharray}
      />
    </svg>
  );
}

export default function LeftPanel({
  selectedIndicatorId, selectedTract, allData,
  indicators,
  comparisonCounty, comparisonData,
  selectedYear, onYearSelect,
  tractTrendData,
  goal,
  rationale,
  visibleYears
}) {
  const config = useDashboardConfig();
  const isMobile = window.innerWidth <= 768;
  const [geoOpen, setGeoOpen] = useState(!isMobile);
  const [trendOpen, setTrendOpen] = useState(true);

  const allDataSafe = allData || new Map();

  const indicatorConfig =
    (indicators ?? config.indicators).find(i => i.id === selectedIndicatorId) ??
    config.indicators.find(i => i.id === selectedIndicatorId);
  const result = allDataSafe.get(selectedIndicatorId);

  // True when the data source only has county-level aggregates (e.g. CDC PLACES
  // county dataset) — geography GEOIDs are 5-digit county FIPS, not 11-digit tract.
  const isCountyLevel = (result?.geography ?? []).some(g => g.geoid?.length === 5);

  const parts = config.org.geography.label.split(',');
  const countyLabel = parts[0]?.trim() ?? config.org.geography.label;
  const stateLabel = parts[1] ? `${parts[1].trim()} Statewide` : 'Statewide';

  // CDC Places data is only available for the year(s) present in the dataset.
  // When the user has selected a year that CDC doesn't have, suppress values rather
  // than silently showing stale data.
  const isCDC = indicatorConfig?.source === 'cdc';
  const cdcAvailableYears = useMemo(
    () => isCDC ? [...new Set((result?.trend ?? []).map(t => t.year))].sort((a, b) => b - a) : [],
    [isCDC, result]
  );
  const cdcYearMismatch = isCDC && !!selectedYear && cdcAvailableYears.length > 0
    && !cdcAvailableYears.includes(selectedYear);

  const countyValueForYear = cdcYearMismatch
    ? null
    : (getYearValue(result?.trend, selectedYear) ?? result?.countyValue ?? null);
  const stateValueForYear = cdcYearMismatch
    ? null
    : (getYearValue(result?.stateTrend, selectedYear) ?? result?.stateValue ?? null);

  const tractValue = useMemo(() => {
    if (!selectedTract || !result?.geography || cdcYearMismatch) return null;
    const row = result.geography.find(g => g.geoid === selectedTract.geoid);
    return row?.value ?? null;
  }, [selectedTract, result, cdcYearMismatch]);

  const bars = useMemo(() => {
    const b = [
      { name: stateLabel, value: stateValueForYear, color: '#8ec49a' },
      { name: countyLabel, value: countyValueForYear, color: config.branding.primaryColor }
    ];
    if (comparisonData && comparisonCounty) {
      b.splice(1, 0, {
        name: comparisonCounty.name,
        value: comparisonData.get(selectedIndicatorId)?.countyValue ?? null,
        color: '#6dcee0'
      });
    }
    if (!isCountyLevel && selectedTract) {
      b.push({
        name: selectedTract.name ?? 'Selected Tract',
        value: tractValue,
        color: config.branding.accentColor
      });
    }
    return b;
  }, [stateValueForYear, countyValueForYear, countyLabel, stateLabel, selectedTract,
    tractValue, config.branding, comparisonCounty, comparisonData, selectedIndicatorId,
    isCountyLevel]);

  // Merge all trend series by year for the trend chart
  const mergedTrend = useMemo(() => {
    const yearMap = new Map();
    (result?.trend ?? []).forEach(p => {
      yearMap.set(p.year, { year: p.year, countyValue: p.value, stateValue: null, comparisonValue: null, tractValue: null });
    });
    (result?.stateTrend ?? []).forEach(p => {
      const ex = yearMap.get(p.year) ?? { year: p.year, countyValue: null, comparisonValue: null, tractValue: null };
      yearMap.set(p.year, { ...ex, stateValue: p.value });
    });
    if (comparisonData && comparisonCounty) {
      (comparisonData.get(selectedIndicatorId)?.trend ?? []).forEach(p => {
        const ex = yearMap.get(p.year) ?? { year: p.year, countyValue: null, stateValue: null, tractValue: null };
        yearMap.set(p.year, { ...ex, comparisonValue: p.value });
      });
    }
    if (!isCountyLevel && tractTrendData && selectedTract) {
      (tractTrendData.get(selectedIndicatorId)?.trend ?? []).forEach(p => {
        const ex = yearMap.get(p.year) ?? { year: p.year, countyValue: null, stateValue: null, comparisonValue: null };
        yearMap.set(p.year, { ...ex, tractValue: p.value });
      });
    }
    const sorted = [...yearMap.values()].sort((a, b) => a.year - b.year);
    if (visibleYears && visibleYears.length > 0) {
      return sorted.filter(p => visibleYears.includes(p.year));
    }
    return sorted;
  }, [result, comparisonData, comparisonCounty, tractTrendData, selectedTract, selectedIndicatorId, visibleYears, isCountyLevel]);

  const hasGoal = typeof goal === 'number' && !isNaN(goal);
  const hasTrend = mergedTrend.length >= 2;
  const chartData = bars.map(b => ({ ...b, chartValue: b.value ?? 0 }));
  const chartHeight = bars.length * 36 + (hasGoal ? 28 : 16);

  // Extend bar axis domain to include goal so the reference line is always visible
  const barValues = bars.map(b => b.value).filter(v => v !== null);
  const barMax = barValues.length ? Math.max(...barValues) : 0;
  const barDomain = hasGoal ? [0, Math.max(barMax, goal) * 1.05] : [0, 'dataMax'];

  if (!indicatorConfig) return null;

  function handleTrendClick(chartData) {
    const year = chartData?.activePayload?.[0]?.payload?.year;
    if (year) onYearSelect(year);
  }

  return (
    <div className="left-panel">
      <div className="lp-header">
        <div className="lp-indicator-name">
          {indicatorConfig.label}
          <span className="lp-source-badge">
            {indicatorConfig.source === 'cdc'
              ? (result?.granularity === 'tract' ? 'CDC Places (Tract)' : 'CDC Places')
              : indicatorConfig.source === 'svi' ? 'CDC/ATSDR SVI'
              : indicatorConfig.source === 'ejscreen' ? 'EPA EJSCREEN'
              : indicatorConfig.source === 'education' ? 'NCES'
              : 'Census ACS'}
          </span>
          {(() => {
            const displayYear = selectedYear !== null
              ? selectedYear
              : result?.trend?.length > 0
                ? Math.max(...result.trend.map(t => t.year))
                : null;
            return displayYear
              ? <span className="lp-year-pill">{displayYear}</span>
              : null;
          })()}
        </div>
        <div className="lp-rationale">
          {rationale ?? indicatorConfig.rationale}
          {indicatorConfig.dataSource && (
            <span className="lp-data-source"> ({indicatorConfig.dataSource})</span>
          )}
        </div>
      </div>

      {isCountyLevel && !cdcYearMismatch && (
        <div className="lp-county-notice">
          Data available at county level only — census tract breakdown not available for this indicator.
        </div>
      )}

      {cdcYearMismatch && (
        <div className="lp-cdc-year-notice">
          CDC Places data is only available for{' '}
          {cdcAvailableYears.length === 1
            ? cdcAvailableYears[0]
            : cdcAvailableYears.join(', ')}.
          {' '}Select {cdcAvailableYears[0]} in the year filter to view this indicator.
        </div>
      )}

      <div className="lp-kpis">
        <div className="lp-kpi-tile lp-kpi-tile--primary">
          <div className="lp-kpi-value">
            {formatValue(countyValueForYear, indicatorConfig.unit)}
          </div>
          <div className="lp-kpi-label">{countyLabel}</div>
        </div>

        <div className="lp-kpi-tile">
          <div className="lp-kpi-value" style={{ color: '#333' }}>
            {formatValue(stateValueForYear, indicatorConfig.unit)}
          </div>
          <div className="lp-kpi-label" style={{ color: '#666' }}>{stateLabel}</div>
        </div>

        {!isCountyLevel && (
          selectedTract ? (
            <div className="lp-kpi-tile lp-kpi-tile--accent">
              <div className="lp-kpi-value">
                {formatValue(tractValue, indicatorConfig.unit)}
              </div>
              <div className="lp-kpi-label">{selectedTract.name ?? 'Selected Tract'}</div>
            </div>
          ) : (
            <div className="lp-kpi-tile lp-kpi-tile--muted">
              <div className="lp-kpi-value" style={{ fontSize: '0.85rem', fontWeight: 500, lineHeight: 1.3 }}>
                —
              </div>
              <div className="lp-kpi-label">Click a tract to compare</div>
            </div>
          )
        )}
      </div>

      <div className="lp-chart">
        <button className="lp-section-toggle" onClick={() => setGeoOpen(o => !o)}>
          <span className="lp-chart-title">Geographic Comparison</span>
          <span className="lp-section-chevron">{geoOpen ? '▾' : '▸'}</span>
        </button>
        {geoOpen && (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart
              layout="vertical"
              data={chartData}
              margin={{ top: hasGoal ? 16 : 4, right: 72, left: 4, bottom: 4 }}
            >
              <XAxis type="number" hide domain={barDomain} />
              <YAxis
                type="category"
                dataKey="name"
                width={130}
                tick={{ fontSize: 11, fontFamily: config.branding.fontBody, fill: '#444' }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                formatter={(_, __, props) => [
                  formatValue(props.payload.value, indicatorConfig.unit),
                  indicatorConfig.label
                ]}
              />
              {hasGoal && (
                <ReferenceLine
                  x={goal}
                  stroke={config.branding.accentColor}
                  strokeDasharray="4 3"
                  strokeWidth={2}
                  label={
                    <GoalLabel
                      goal={goal}
                      unit={indicatorConfig.unit}
                      accentColor={config.branding.accentColor}
                      fontFamily={config.branding.fontBody}
                    />
                  }
                />
              )}
              <Bar dataKey="chartValue" radius={[0, 3, 3, 0]} maxBarSize={18}>
                <LabelList
                  dataKey="value"
                  position="right"
                  formatter={v => formatValue(v, indicatorConfig.unit)}
                  style={{ fontSize: '11px', fontFamily: config.branding.fontBody, fill: '#333' }}
                />
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={entry.value !== null ? entry.color : '#e0e0e0'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {!hasTrend && (
        <div className="lp-no-trend">
          Longitudinal data not available for this indicator.
        </div>
      )}

      {hasTrend && (
        <div className="lp-trend">
          <button className="lp-section-toggle" onClick={() => setTrendOpen(o => !o)}>
            <span className="lp-trend-title">Trend over time</span>
            <span className="lp-section-chevron">{trendOpen ? '▾' : '▸'}</span>
          </button>
          {trendOpen && (<><ResponsiveContainer width="100%" height={150}>
            <LineChart
              data={mergedTrend}
              margin={{ top: 8, right: 16, left: 0, bottom: 4 }}
              onClick={handleTrendClick}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eeeeee" vertical={false} />
              <XAxis
                dataKey="year"
                tick={{ fontSize: 10, fontFamily: config.branding.fontBody, fill: '#999' }}
                tickLine={false}
                axisLine={false}
                interval={0}
              />
              <YAxis hide domain={['auto', 'auto']} />
              <Tooltip
                formatter={(v, name) => [
                  formatValue(v, indicatorConfig.unit),
                  name === 'countyValue' ? countyLabel
                    : name === 'stateValue' ? stateLabel
                    : name === 'comparisonValue' ? (comparisonCounty?.name ?? 'Comparison')
                    : name === 'tractValue' ? (selectedTract?.name ?? 'Selected Tract')
                    : name
                ]}
                labelFormatter={y => `${y}`}
                contentStyle={{ fontSize: '11px', padding: '4px 8px', borderRadius: '4px' }}
              />

              {/* State — dashed sage green */}
              <Line
                type="monotone"
                dataKey="stateValue"
                stroke="#8ec49a"
                strokeWidth={1.5}
                strokeDasharray="4 3"
                dot={false}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />

              {/* Comparison county — teal, short-dash */}
              {comparisonCounty && comparisonData && (
                <Line
                  type="monotone"
                  dataKey="comparisonValue"
                  stroke="#6dcee0"
                  strokeWidth={1.5}
                  strokeDasharray="2 2"
                  dot={false}
                  activeDot={{ r: 4 }}
                  isAnimationActive={false}
                />
              )}

              {/* Selected tract — accent color, dash-dot — only for tract-level data */}
              {!isCountyLevel && selectedTract && tractTrendData && (
                <Line
                  type="monotone"
                  dataKey="tractValue"
                  stroke={config.branding.accentColor}
                  strokeWidth={1.5}
                  strokeDasharray="6 2 2 2"
                  dot={false}
                  activeDot={{ r: 4 }}
                  isAnimationActive={false}
                />
              )}

              {hasGoal && (
                <ReferenceLine
                  y={goal}
                  stroke={config.branding.accentColor}
                  strokeDasharray="5 3"
                  strokeWidth={1.5}
                  label={{
                    value: formatValue(goal, indicatorConfig.unit),
                    position: 'insideTopRight',
                    fontSize: 10,
                    fill: config.branding.accentColor,
                    fontFamily: config.branding.fontBody
                  }}
                />
              )}

              {/* Home county — primary, selected year highlighted */}
              <Line
                type="monotone"
                dataKey="countyValue"
                stroke={config.branding.primaryColor}
                strokeWidth={2.5}
                isAnimationActive={false}
                dot={(props) => {
                  const { cx, cy, payload } = props;
                  const isSelected = payload.year === selectedYear;
                  return (
                    <circle
                      key={`lp-dot-${payload.year}`}
                      cx={cx} cy={cy}
                      r={isSelected ? 6 : 3}
                      fill={isSelected ? config.branding.accentColor : config.branding.primaryColor}
                      stroke="#fff"
                      strokeWidth={2}
                    />
                  );
                }}
                activeDot={{ r: 7, stroke: '#fff', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>

          <div className="lp-trend-legend">
            <span className="lp-trend-legend-item">
              <LegendLine stroke={config.branding.primaryColor} strokeWidth={2.5} />
              {countyLabel}
            </span>
            <span className="lp-trend-legend-item">
              <LegendLine stroke="#8ec49a" strokeWidth={1.5} strokeDasharray="4 3" />
              {stateLabel}
            </span>
            {comparisonCounty && comparisonData && (
              <span className="lp-trend-legend-item">
                <LegendLine stroke="#6dcee0" strokeWidth={1.5} strokeDasharray="2 2" />
                {comparisonCounty.name}
              </span>
            )}
            {!isCountyLevel && selectedTract && tractTrendData && (
              <span className="lp-trend-legend-item">
                <LegendLine stroke={config.branding.accentColor} strokeWidth={1.5} strokeDasharray="6 2 2 2" />
                {selectedTract.name ?? 'Selected Tract'}
              </span>
            )}
          </div>
          </>)}
        </div>
      )}
    </div>
  );
}
